import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Backdrop, CircularProgress } from '@mui/material';
import React, { useState, createContext, ReactNode, useContext, useEffect } from 'react';
import { api } from '../../api';
import { loginRequest } from '../../azure/authConfig';
import menuItems, { MenuItemType } from '../../menu-items';
import { LoginAPI } from '../../schema/external';
import { UserPagePermission } from '../../schema/permission';
import { User, Roles } from '../../schema/user';

interface authContextInterface {
  user: User | null;
  permission: Record<string, boolean>;
  isLoading: boolean;
  isAuthenticated: boolean;
  isFinishAuthenCheck: boolean;
  error: string;
  // role: Roles | null;
  signIn: (username: string, password: string) => Promise<void>;
  signInWithAD: () => Promise<void>;
  signInWithToken: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const authContext = createContext<authContextInterface | null>(null);

export const useProvideAuth = () => {
  const { instance, accounts, inProgress } = useMsal();

  const [isFinishAuthenCheck, setIsFinishAuthenCheck] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [permission, setPermission] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string>('');

  const verifyUser = (token?: string) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    return api
      .post(
        '/token',
        { token: token },
        {
          withCredentials: true
        }
      )
      .then((res) => {
        const { data } = res;
        setAuthenticated(true);
        const user = data.user;
        setUser(user);
        setError('');
        api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      })
      .catch(() => {
        delete api.defaults.headers.common['Authorization'];
        setAuthenticated(false);
        setUser(null);
        if (isFinishAuthenCheck) {
          setError('เข้าสู่ระบบไม่สําเร็จ');
        }
      })
      .finally(() => {
        setIsLoading(false);
        setIsFinishAuthenCheck(true);
      });
  };

  const verifyADUser = (token: string) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    return api
      .post(
        '/token/ad',
        { token: token },
        {
          withCredentials: true
        }
      )
      .then((res) => {
        const { data } = res;
        setAuthenticated(true);
        const user = data.user;
        setUser(user);
        setError('');
        api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      })
      .catch(() => {
        delete api.defaults.headers.common['Authorization'];
        setAuthenticated(false);
        setUser(null);
        setError('เข้าสู่ระบบด้วย PTT AD ไม่สําเร็จ');
      })
      .finally(() => {
        setIsLoading(false);
        setIsFinishAuthenCheck(true);
      });
  };

  // const verifyADUserWithEmail = (email: string) => {
  //   if (isLoading) {
  //     return;
  //   }
  //   setIsLoading(true);
  //   return api
  //     .post(
  //       '/token/ad_with_email',
  //       { email: email },
  //       {
  //         withCredentials: true
  //       }
  //     )
  //     .then((res) => {
  //       const { data } = res;
  //       setAuthenticated(true);
  //       const user = data.user;
  //       setUser(user);
  //       setError('');
  //       api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
  //     })
  //     .catch(() => {
  //       delete api.defaults.headers.common['Authorization'];
  //       setAuthenticated(false);
  //       setUser(null);
  //       setError('เข้าสู่ระบบด้วย PTT AD ไม่สําเร็จ');
  //     })
  //     .finally(() => setIsLoading(false));
  // };

  // const signIn = (username: string, password: string) => {
  //   setError('');
  //   setIsFinishAuthenCheck(true);
  //   return api
  //     .post<LoginAPI>(
  //       '/login',
  //       {
  //         loginId: username,
  //         password: password,
  //         applicationId: process.env.REACT_APP_AUTH_APPLICATION_ID
  //       },
  //       {
  //         headers: {
  //           Authorization: process.env.REACT_APP_AUTH_API_KEY || '',
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     )
  //     .then(async (res) => {
  //       const { data } = res;
  //       if (data) {
  //         await verifyUser(data.token);
  //       } else {
  //         delete api.defaults.headers.common['Authorization'];
  //         setAuthenticated(false);
  //         setUser(null);
  //       }
  //       return;
  //     })
  //     .catch(() => {
  //       delete api.defaults.headers.common['Authorization'];
  //       setAuthenticated(false);
  //       setUser(null);
  //       setError('เข้าสู่ระบบไม่สําเร็จ Username หรือ รหัสผ่าน ไม่ถูกต้อง');
  //     });
  // };

  const signIn = (username: string, password: string) => {
    setError('');
    setIsFinishAuthenCheck(true);
    const form = new FormData();
    form.append('username', username);
    form.append('password', password);
    return api
      .post('/login', form, {
        withCredentials: true
      })
      .then((res) => {
        const { data } = res;
        setAuthenticated(true);
        const user = data.user;
        setUser(user);
        setError('');
        api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      })
      .catch(() => {
        delete api.defaults.headers.common['Authorization'];
        setAuthenticated(false);
        setUser(null);
        setError('เข้าสู่ระบบไม่สําเร็จ');
      })
      .finally(() => {
        setIsLoading(false);
        setIsFinishAuthenCheck(true);
      });
  };

  const signInWithAD = async () => {
    setError('');
    setIsFinishAuthenCheck(true);
    return instance.loginRedirect(loginRequest);
  };

  const signInWithToken = async (token: string) => {
    setError('');
    setIsFinishAuthenCheck(true);
    if (token) {
      await verifyUser(token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      setAuthenticated(false);
      setUser(null);
    }
  };

  const signOut = async () => {
    return api
      .post('/logout', {}, { withCredentials: true })
      .then(async () => {
        delete api.defaults.headers.common['Authorization'];
        setAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      })
      .catch(() => {
        delete api.defaults.headers.common['Authorization'];
        setAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      })
      .finally(() => {
        if (accounts.length != 0) {
          instance.logoutRedirect();
        }
      });
  };

  useEffect(() => {
    // instance.logoutRedirect();
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (accounts.length != 0) {
      // const adUser = accounts[0];
      // verifyADUserWithEmail(adUser.username).finally(() => setIsLoading(false));
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        })
        .then((response) => {
          verifyADUser(response.accessToken);
        })
        .catch(() => {
          // Catch interaction_required errors and call interactive method to resolve
          setError('เข้าสู่ระบบไม่สําเร็จ เกิดข้อผิดพลาดภายในระบบ PTT AD');
          setIsLoading(false);
          setIsFinishAuthenCheck(true);
        });
      return;
    }
    if (!isFinishAuthenCheck) {
      verifyUser();
    }
  }, [accounts, inProgress]);

  useEffect(() => {
    if (user) {
      setIsLoading(true);
      api.get<UserPagePermission[]>(`/permission/${user.role_id}`).then((res) => {
        const { data } = res;
        const temp: Record<string, boolean> = {};
        const addToTemp = (item: MenuItemType) => {
          temp[item.id] = false;
          if (item.children) {
            item.children.forEach((element) => {
              addToTemp(element);
            });
          }
          return;
        };
        menuItems.items.forEach((item) => {
          addToTemp(item);
        });
        data.forEach((permit) => {
          if (permit.page_id) {
            temp[permit.page_id] = permit.is_allowed;
          }
        });
        setPermission(temp);
        setIsLoading(false);
      });
    }
  }, [user]);

  return {
    user,
    permission,
    isLoading,
    isAuthenticated,
    isFinishAuthenCheck,
    error,
    // role,
    signIn,
    signInWithAD,
    signInWithToken,
    signOut
  };
};

export const useAuth = () => {
  return useContext(authContext);
};

export const ProvideAuth = ({ children }: { children: ReactNode }) => {
  const auth = useProvideAuth();
  if (auth.isFinishAuthenCheck) {
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#ACCEF7',
        backgroundSize: 'cover',
        background: 'white'
      }}
    >
      <CircularProgress color="inherit" />
    </div>
  );
};
